import { faArrowUpRightFromSquare, faBed, faCheckCircle, faCreditCard, faGlobe, faLevelUp, faPerson, faPlus, faSpinner, faUser, faVideo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Card, Col, Container, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { getWSService } from "../../../amplify/api/llm-web-socket";
import { callDashboardLLM, callGetDashboardLLMStatus, LLMDashboardStatus } from "../../../amplify/appsync/dashboard-llm";
import { callFhirReasonsForVisit } from "../../../amplify/appsync/fetch-sync-fhir";
import { callQuery, FollowedPatient, GET_ALL_VERSIONS_FOR_CSN_AND_FILE_TYPE, GET_FOLLOWED_PATIENTS_BY_USERNAME_AND_CSN, GET_LATEST_VERSIONS_FOR_CSN, LastViewedPatientFile, LLM_FILE_DIFF, LLM_VISIT_FEEDBACK_BY_CSN, LLMVisitFeedback, LOCATIONS_BY_CSN, NEXT_STEPS_BY_CSN, NextStep, Note, NOTES_FOR_USERNAME_AND_MRN, PatientFileVersion, UNREAD_EXPLAIN_JOBS } from "../../../amplify/appsync/query";
import { PatientVisit, PatientVisitMetadata } from "../../../amplify/appsync/search-patients";
import { callWrite, DELETE_FOLLOWED_PATIENT, EXPLAIN_CHARTING, EXPLAIN_CURRENT_VISIT, EXPLAIN_DDX, EXPLAIN_PATIENT_SUMMARY, UPDATE_LAST_VIEWED, INSERT_FOLLOWED_PATIENT, INSERT_NOTE, SCAN_MULTI, EXPLAIN_REVIEW_REVISIT, EXPLAIN_REVIEW_ADMIT_DC } from "../../../amplify/appsync/write";
import { getUserFriendlyDate } from "../../../utils/time";
import useSetPagePatientVisit from "../../common/GlobalOptionsContext";
import { dashboardLLMCache } from "../../dashboard/llm/cache";
import { AdditionalContextModal } from "../../dashboard/llm/common/AdditionalContextModal";
import { ScanMultiModal } from "../../dashboard/llm/common/ScanMultiModal";
import { SectionCurrentVisit } from "../../dashboard/llm/notes/SectionCurrentVisit";
import { SectionPatientSummary } from "../../dashboard/llm/notes/SectionPatientSummary";
import { ExpandingTextarea } from "../../input/ExpandingTextArea";
import { SectionTrackboardDDX } from "./SectionTrackboardDDX";
import { SectionTrackboardNextSteps } from "./SectionTrackboardNextSteps";
import { SectionTrackboardTimeline } from "./SectionTrackboardTimeline";
import { SectionTrackboardVerticalVideo } from "./SectionTrackboardVerticalVideo";

import { v4 as uuidv4 } from 'uuid';
import { callLLMDashboardStreamable } from "../../../amplify/api/llm-chat";
import React from "react";
import { jsonrepair } from 'jsonrepair';
import { getFileFromKey } from "../../dashboard/llm/common/utils";
import { trackEvent } from "../../../amplify/appsync/user-events";
import { ExplainRevisitDashboard } from "../../llm-visit/dashboard/ExplainRevisitDashboard";
import { ExplainAdmitDCDashboard } from "../../llm-visit/dashboard/ExplainAdmitDCDashboard";
import { ExplainEDDDashboard } from "../../llm-visit/dashboard/ExplainEDDDashboard";
import { SectionDDX } from "../../dashboard/llm/notes/SectionDDX";
import PersonalIdentifier from "../../common/PersonalIdentifier";
import { VerticalVideoModal } from "../../common/VerticalVideoModal";
import { DDX } from "../../dashboard/llm/common/models";
import { isLobby } from "../../dashboard/llm/utils";

const JOB_CONFIG = [
    {
        key: "current_visit_presentation",
        file: EXPLAIN_CURRENT_VISIT,
    },
    {
        key: "patient_summary",
        file: EXPLAIN_PATIENT_SUMMARY,
    },
    {
        key: "ddx",
        file: EXPLAIN_DDX,
    },
    {
        key: "charting",
        file: EXPLAIN_CHARTING,
    },
];

let socketConnection: any;
let sessionId: string;

interface SectionQuickViewProps {
    className?: string,
    patient: PatientVisit & PatientVisitMetadata,
    updatePatientWithNote: any,
    showPatientMetadata: boolean,
    handleFollowStatusChange?: any,
    user: any,
    tenant: any,
    ws: any,
    eddSurveyResultsUpdateCallback?: (csn: string, results: Record<string, any>) => void,
    admitSurveyResultsUpdateCallback?: (csn: string, results: Record<string, any>) => void,
    revisitSurveyResultsUpdateCallback?: (csn: string, results: Record<string, any>) => void,
}

export const SectionQuickView = React.memo((props: SectionQuickViewProps) => {

    const [showRegenerateContextMenu, setShowRegenerateContextMenu] = useState(false);

    const [isLoadingLocations, setIsLoadingLocations] = useState(false);
    const [isSavingNote, setIsSavingNote] = useState(false);
    const [noteText, setNoteText] = useState(props.patient && props.patient.note ? props.patient.note : "");
    const [saveTimeout, setSaveTimeout] = useState<any>(null);
    const [lastSavedNoteText, setLastSavedNoteText] = useState(props.patient && props.patient.note ? props.patient.note : "");
    const [isNoteTextChanged, setIsNoteTextChanged] = useState(false);
    const [pastNotes, setPastNotes] = useState<Note[]>([]);
    const [locations, setLocations] = useState<any[]>([]);
    const [ddxActive, setDdxActive] = useState<number>(0);

    const [statuses, setStatuses] = useState<Record<string, LLMDashboardStatus>>({});
    const [feedbacks, setFeedbacks] = useState<LLMVisitFeedback[]>([]);
    const [existingNextSteps, setExistingNextSteps] = useState<NextStep[]>([]);
    const [latestFileVersions, setLatestFileVersions] = useState<Record<string, PatientFileVersion>>();
    const [reasonsForVisit, setReasonsForVisit] = useState<string>();

    const [currentVisitPresentation, setCurrentVisitPresentation] = useState<any>();
    const [patientSummary, setPatientSummary] = useState<any>();
    const [ddx, setDdx] = useState<DDX>();
    const [charting, setCharting] = useState<any>();

    const [isScanMultiModalOpen, setIsScanMultiModalOpen] = useState(false);
    const [unreadFileVersionMap, setUnreadFileVersionMap] = useState<Record<string, PatientFileVersion>>();

    const [isFollowing, setIsFollowing] = useState<boolean>(false);
    const [isLoadingFollowing, setIsLoadingFollowing] = useState<boolean>(true);
    const [isSavingFollowing, setIsSavingFollowing] = useState<boolean>(false);

    const [selectedScanMultiVersion, setSelectedScanMultiVersion] = useState<any>();
    const [selectedInitialSection, setSelectedInitialSection] = useState<string>();
    const [selectedTriggeringEvents, setSelectedTriggeringEvents] = useState<string[]>();

    // 
    // Preparing environment
    // 

    const regenerateKeyRef = useRef<any>();
    const [isWebSocketConnecting, setIsWebSocketConnecting] = useState<any>(false);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [regenerateTarget, setRegenerateTarget] = useState<string | undefined>();

    const [isLoadingScanMulti, setIsLoadingScanMulti] = useState<boolean>(false);
    const [isLoadingCurrentVisitPresentation, setIsLoadingCurrentVisitPresentation] = useState<boolean>(false);
    const [isLoadingPatientSummary, setIsLoadingPatientSummary] = useState<boolean>(false);
    const [isLoadingDdx, setIsLoadingDdx] = useState<boolean>(false);
    const [isLoadingCharting, setIsLoadingCharting] = useState<boolean>(false);

    // Sets the patient context for the Assistant
    //
    useSetPagePatientVisit(props.patient);

    const sectionToIsLoading: { [key: string]: boolean } = {
        scan_multi: isLoadingScanMulti,
        current_visit_presentation: isLoadingCurrentVisitPresentation,
        patient_summary: isLoadingPatientSummary,
        ddx: isLoadingDdx,
        charting: isLoadingCharting,
    };

    const sectionToSetIsLoading: { [key: string]: (val: any) => void } = {
        scan_multi: setIsLoadingScanMulti,
        current_visit_presentation: setIsLoadingCurrentVisitPresentation,
        patient_summary: setIsLoadingPatientSummary,
        ddx: setIsLoadingDdx,
        charting: setIsLoadingCharting,
    };

    const sectionToGetter: { [key: string]: any } = {
        current_visit_presentation: currentVisitPresentation,
        patient_summary: patientSummary,
        ddx: ddx,
        charting: charting,
    };

    const sectionToSetter: { [key: string]: (val: any) => void } = {
        current_visit_presentation: setCurrentVisitPresentation,
        patient_summary: setPatientSummary,
        ddx: setDdx,
        charting: setCharting,
    };

    const loadPrecomputedExplanations = async (patient: PatientVisit) => {
        const unavailableExplanations: string[] = [];

        const sections = JOB_CONFIG;

        for (let section of sections) {
            sectionToSetIsLoading[section.key]?.(true);
            const cachedCurrentVisit = await dashboardLLMCache.get(section.file, patient, props.tenant);
            if (cachedCurrentVisit) {
                sectionToSetter[section.key]?.(cachedCurrentVisit);
            } else {
                unavailableExplanations.push();
            }
            sectionToSetIsLoading[section.key]?.(false);
        }
    };

    // Effect to handle autosave on content change
    useEffect(() => {
        // Clear any existing timeout if content changes
        if (saveTimeout) {
            clearTimeout(saveTimeout);
        }

        // Set a new timeout to save the content after 2 seconds of inactivity
        const newTimeout = setTimeout(() => {
            if (isNoteTextChanged) {
                saveNote();
            }
        }, 2000);

        // Store the timeout ID so it can be cleared later
        setSaveTimeout(newTimeout);

        // Clean up the timeout when the component unmounts or content changes
        return () => {
            clearTimeout(newTimeout);
        };
    }, [noteText]); // Only trigger effect when content changes

    useEffect(() => {
        setIsLoadingLocations(true);

        loadPrecomputedExplanations(props.patient);

        callQuery({
            operation: LOCATIONS_BY_CSN,
            csn: props.patient.csn,
        })
        .then((locations: any) => {
            setLocations(locations);
            setIsLoadingLocations(false);
        });

        callQuery({
            operation: GET_FOLLOWED_PATIENTS_BY_USERNAME_AND_CSN,
            csn: props.patient.csn,
            username: props.user.username,
        })
        .then(async (patients: FollowedPatient[]) => {
            if (patients.length > 0) {
                setIsFollowing(true);
            } else {
                setIsFollowing(false);
            }
        })
        .finally(() => {
            setIsLoadingFollowing(false);
        });

        callQuery({
            operation: NOTES_FOR_USERNAME_AND_MRN,
            mrn: props.patient.mrn,
        })
        .then((notes: Note[]) => {
            const filteredNotes = notes.filter(note => note.csn !== props.patient.csn);
            setPastNotes(filteredNotes);
        });

        callQuery({
            operation: LLM_VISIT_FEEDBACK_BY_CSN,
            csn: props.patient.csn,
        }).then(feedbacks => {
            setFeedbacks(feedbacks);
        });

        callQuery({
            operation: UNREAD_EXPLAIN_JOBS,
            csn: props.patient.csn,
        }).then((unreadFileVersions: PatientFileVersion[]) => {
            const fileVersionMap: Record<string, PatientFileVersion> = {};
            for (let fv of unreadFileVersions) {
                fileVersionMap[fv.file_type] = fv;
            }
            setUnreadFileVersionMap(fileVersionMap);

            callWrite({
                operation: UPDATE_LAST_VIEWED,
                csn: props.patient.csn,
                jobs: [
                    ...JOB_CONFIG.map(config => config.file),
                    EXPLAIN_REVIEW_ADMIT_DC,
                    EXPLAIN_REVIEW_REVISIT,
                ],
            }).then((result: any) => {
                console.log("Completed UPDATE_LAST_VIEWED");
            });
        });

        callQuery({
            operation: GET_LATEST_VERSIONS_FOR_CSN,
            csn: props.patient.csn,
        }).then((latestVersions: PatientFileVersion[]) => {
            const fileTypeToVersion: any = {};
            for (let latestVersion of latestVersions) {
                fileTypeToVersion[latestVersion.file_type] = latestVersion;
            }
            setLatestFileVersions(fileTypeToVersion);
        });

        callQuery({
            operation: NEXT_STEPS_BY_CSN,
            csn: props.patient.csn,
        }).then((nextSteps: NextStep[]) => {
            for (let nextStep of nextSteps) {
                if (!nextStep.updated_date) {
                    nextStep.updated_date = nextStep.creation_date
                }
            }
            setExistingNextSteps(nextSteps);
        });

        if (props.patient.fhir_id) {
            callFhirReasonsForVisit({
                csn: props.patient.csn,
                fhir_id: props.patient.fhir_id,
            }).then((output: any) => {
                setReasonsForVisit(JSON.stringify(output));
            });
        }

        callGetDashboardLLMStatus(props.patient.csn).then((statuses) => {
            setStatuses(statuses);
        });

        trackEvent(`trackboard_expand_details?internal_csn=${props.patient.internal_csn}`, props.user?.username);
    }, [props.patient]);

    const handleNoteChange = (text: any) => {
        setIsNoteTextChanged(true);
        setNoteText(text);
    };

    const saveNote = () => {
        if (lastSavedNoteText !== noteText) {
            setIsSavingNote(true);
            callWrite({
                operation: INSERT_NOTE,
                creation_time: new Date().getTime(),
                note_type: "note",
                csn: props.patient.csn,
                mrn: props.patient.mrn,
                note: noteText,
            })
            .then((output: Note[]) => {
                console.log("Saved note", output);
                setIsSavingNote(false);
                setNoteText(output[0].note);
                props.updatePatientWithNote(output[0]);
                setLastSavedNoteText(output[0].note);
            });
        }
    }

    // Update the ref whenever regenerateTarget changes
    useEffect(() => {
        let config = null;
        for (let i = 0; i < JOB_CONFIG.length; i++) {
            if (regenerateTarget && JOB_CONFIG[i].file !== regenerateTarget) {
                continue;
            }
            config = JOB_CONFIG[i];
        }
        regenerateKeyRef.current = config?.key;
    }, [regenerateTarget]);

    // Store accumulated text in a ref so we don't trigger extra re-renders
    const accumulatedTextRef = useRef("");
    const lastSeqRef = useRef(0);

    const handleChatUpdate = useCallback((response: any) => {
        if (
            response &&
            typeof response === "object" &&
            !Array.isArray(response) &&
            (response["role"] === "COMPLETED" || response["role"] === "COMPLETED_WITH_ERROR")
        ) {
            // End of stream cleanup
            if (props.patient && regenerateKeyRef.current) {
                callQuery({
                    operation: GET_LATEST_VERSIONS_FOR_CSN,
                    csn: props.patient.csn,
                }).then((latestVersions: PatientFileVersion[]) => {
                    const fileTypeToVersion: any = {};
                    for (let latestVersion of latestVersions) {
                        fileTypeToVersion[latestVersion.file_type] = latestVersion;
                    }
                    setLatestFileVersions(fileTypeToVersion);
                });
                // console.log("Running dashboardLLMCache ", regenerateKeyRef.current)
                dashboardLLMCache.get(getFileFromKey(JOB_CONFIG, regenerateKeyRef.current), props.patient, props.tenant).then((cachedCurrentVisit) => {
                    sectionToSetter[regenerateKeyRef.current]?.(cachedCurrentVisit);
                    sectionToSetIsLoading[regenerateKeyRef.current](false);
                });
                callGetDashboardLLMStatus(props.patient.csn).then((statuses) => {
                    setStatuses(statuses);
                });
            }
        }
        // Oct 2025: Disable streaming because time-to-first token is high for Azure OpenAI models
        //           which leads to a degraded/non-existent stream. Re-enable once we move back to 
        //           native OpenAI or Claude models.
        //
        // else if (response && typeof response === "object" && "seq" in response && "delta" in response) {
        //     const { seq, delta } = response;

        //     // console.log(`Got seq ${seq} with expected ${lastSeqRef.current + 1} and delta=${delta}`);

        //     // Optional: Check for skipped or out-of-order chunks
        //     if (seq !== lastSeqRef.current + 1) {
        //         console.warn(`⚠ Out of order chunk: expected ${lastSeqRef.current + 1} but got ${seq}`);
        //         // Optionally handle gap recovery here
        //         return;
        //     }

        //     // Append the new delta to accumulated text
        //     accumulatedTextRef.current += delta;
        //     lastSeqRef.current = seq;
        //     // console.log(`accumulatedTextRef=${accumulatedTextRef.current}; lastSeqRef.current=${lastSeqRef.current}`);

        //     // Push the updated text into your UI
        //     if (regenerateKeyRef.current) {
        //         const key: any = regenerateKeyRef.current;
        //         sectionToSetIsLoading[key](false);
        //         try {
        //             sectionToSetter[key](JSON.parse(jsonrepair(accumulatedTextRef.current)));
        //         } catch {
        //             console.warn(`Could not parse the following accumulated text: ${accumulatedTextRef.current}`);
        //             sectionToSetter[key]({});
        //         }
        //     }
        // }
        // else if (regenerateKeyRef.current) {
        //     // Fallback: if the backend sends a plain string (legacy mode)
        //     const key: any = regenerateKeyRef.current;
        //     sectionToSetIsLoading[key](false);
        //     sectionToSetter[key](response);
        // }
    }, [regenerateTarget]);

    const handleWebSocketConnected = useCallback(() => {
        setIsWebSocketConnecting(false);
    }, []);

    const connect = () => {
        setIsWebSocketConnecting(true);
        sessionId = uuidv4();
        socketConnection = getWSService(props.ws, sessionId, handleWebSocketConnected);
        if (socketConnection.sessionId) {
            // This is because getWSService may actually return a previous open web socket with an older connection ID
            sessionId = socketConnection.sessionId;
        }

        if (!socketConnection.hasListener(handleChatUpdate)) {
            socketConnection.addMessageListener(handleChatUpdate);
        }
    }

    const regenerate = async (shouldSyncData: boolean, target?: string) => {
        accumulatedTextRef.current = "";
        lastSeqRef.current = 0;

        // console.log(`Reset lastSeqRef ${lastSeqRef.current + 1}`);

        if (props.patient) {
            setIsRegenerating(true);

            let config = null;

            for (let i = 0; i < JOB_CONFIG.length; i++) {
                if (target && JOB_CONFIG[i].file !== target) {
                    continue;
                }
                if (JOB_CONFIG[i].file.startsWith("scan_")) {
                    continue;
                }
                config = JOB_CONFIG[i];
            }

            connect();

            if (config) {
                sectionToSetIsLoading[config.key]?.(true);
            }

            if (shouldSyncData) {
                const scanMultiResponse = await callDashboardLLM({
                    "operation": SCAN_MULTI,
                    "mrn": props.patient.mrn,
                    "csn": props.patient.csn,
                });
            }
            if (config) {
                await callLLMDashboardStreamable(sessionId, {
                    operation: config.file,
                    mrn: props.patient.mrn,
                    csn: props.patient.csn,
                    report_date_str: new Date().toISOString().split('.')[0] + 'Z',
                });
            }
            setIsRegenerating(false);
        }
    };

    const followPatient = () => {
        setIsSavingFollowing(true);
        if (!props.patient) {
            return;
        }
        if (isFollowing) {
            callWrite({
                operation: DELETE_FOLLOWED_PATIENT,
                csn: props.patient.csn,
                username: props.user.username,
            })
            .then(() => {
                setIsFollowing(false);
                if (props.handleFollowStatusChange) {
                    props.handleFollowStatusChange(props.patient, false);
                }
            })
            .finally(() => {
                setIsSavingFollowing(false);
            });
        } else {
            callWrite({
                operation: INSERT_FOLLOWED_PATIENT,
                csn: props.patient.csn,
                username: props.user.username,
                follow_type: "MANUAL",
            })
            .then(() => {
                setIsFollowing(true);
                if (props.handleFollowStatusChange) {
                    props.handleFollowStatusChange(props.patient, true);
                }
            })
            .finally(() => {
                setIsSavingFollowing(false);
            });
        }
    };

    /**
     * Called when the user wants to see the data snapshot for the LLM section
     */
    const handleShowScanMultiModalForSection = (patientFileVersion?: PatientFileVersion) => {
        if (patientFileVersion) {
            callQuery({
                operation: GET_ALL_VERSIONS_FOR_CSN_AND_FILE_TYPE,
                csn: patientFileVersion.csn,
                file_type: 'scan_multi',
            })
            .then((versions: PatientFileVersion[]) => {
                // Find previous version
                const sortedVersions = [...versions].sort((a, b) => new Date(a.version_date).getTime() - new Date(b.version_date).getTime());
                const selectedIndex = sortedVersions.findIndex(v => v.version_id === patientFileVersion.source_version_id);
                const previousVersion = selectedIndex > 0 ? sortedVersions[selectedIndex - 1] : null;

                setSelectedScanMultiVersion({
                    origVersion: previousVersion ? previousVersion.source_version_id : undefined,
                    newVersion: patientFileVersion.source_version_id,
                });
            });
            setSelectedTriggeringEvents(patientFileVersion.source_changed_keys);
            setIsScanMultiModalOpen(true);
        }
    };

    /**
     * Called when the user selects another version of the LLM section
     */
    const handleFileVersionChange = (fileType: string, fileVersion: PatientFileVersion, previousFileVersion: PatientFileVersion | null) => {
        const updatedFileVersions = {
            ...latestFileVersions,
            [fileType]: {
                ...fileVersion
            },
        };
        setSelectedScanMultiVersion({
            origVersion: previousFileVersion ? previousFileVersion.source_version_id : undefined,
            newVersion: fileVersion.source_version_id,
        })
        setLatestFileVersions(updatedFileVersions);
    }

    return (
        <div className={`${props.className} notes-dashboard`}>
            {
                // diffSinceLastVisit && (
                //     <div className="patient-details-section">
                //         <h6>What's New</h6>
                //         <div className="pill-details-wrapper">
                //             {
                //                 Object.keys(diffSinceLastVisit).map((k) => {
                //                     const value = diffSinceLastVisit[k];
                //                     let count: number | null = null;

                //                     if (Array.isArray(value)) {
                //                         count = value.length;
                //                     } else if (value && typeof value === 'object') {
                //                         count = Object.keys(value).length;
                //                     }

                //                     if (count !== null && count === 0) {
                //                         return <></>
                //                     }

                //                     return (
                //                         <div className="pill-details-btn pill-details-btn-blue" key={k} onClick={() => {
                //                             setSelectedScanMultiVersion(fileVersionsSinceLastVisit);
                //                             setSelectedInitialSection(k);
                //                             setIsScanMultiModalOpen(true);
                //                         }}>
                //                             {k}{count !== null ? ` (${count})` : ''}
                //                         </div>
                //                     );
                //                 })
                //             }
                //             {/* <div className="pill-details-btn pill-details-btn-blue">Labs (6)</div> */}
                //             {/* {JSON.stringify(diffSinceLastVisit)} */}
                //         </div>
                //     </div>
                // )
            }
            <AdditionalContextModal regenerate={regenerate} showModal={showRegenerateContextMenu} setShowModal={setShowRegenerateContextMenu} patientVisit={props.patient} target={regenerateTarget} tenant={props.tenant} />
            {
                props.showPatientMetadata && (
                    <Row className="mb-2">
                        {/* <Col md={2}>
                            <span className="gradient-text" style={{fontSize: '1.25rem'}}><small>Quick View</small></span>
                        </Col> */}
                        <Col md={3}>
                            <div>
                                <FontAwesomeIcon icon={faBed} fixedWidth />{' '}CSN: <strong><PersonalIdentifier identifier={props.patient.csn} /></strong>
                            </div>
                            <div>
                                <FontAwesomeIcon icon={faUser} fixedWidth />{' '}MRN: <strong><PersonalIdentifier identifier={props.patient.mrn} /></strong>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div>
                                <FontAwesomeIcon icon={faLevelUp} fixedWidth />{' '}Level-of-Care: <strong>{props.patient.admit_level_of_care ?? "-"}</strong>
                            </div>
                            <div>
                                <FontAwesomeIcon icon={faCreditCard} fixedWidth />{' '}Payor: <strong>{props.patient.payor_class ?? "-"}</strong>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div>
                                <FontAwesomeIcon icon={faGlobe} fixedWidth />{' '}Ethnicity: <strong>{props.patient.ethnicity ?? "-"}</strong>
                            </div>
                            <div>
                                <FontAwesomeIcon icon={faPerson} fixedWidth />{' '}Race: <strong>{props.patient.race ?? "-"}</strong>
                            </div>
                        </Col>
                    </Row>
                )
            }
            <Row className="mb-2">
                <Col md={10}>
                    {
                        props.patient && (
                            <Button variant="secondary me-2" size="sm" onClick={() => {
                                window.open(`/dashboard/${props.patient.internal_csn}`, "_blank");
                            }}><span className="me-1"><FontAwesomeIcon icon={faArrowUpRightFromSquare} /></span> Show Detailed Dashboard{unreadFileVersionMap ? ` (${Object.keys(unreadFileVersionMap).length} new)` : ""}</Button>
                        )
                    }
                    {
                        !isLoadingFollowing && (
                            <OverlayTrigger
                                placement="bottom"
                                overlay={
                                    <Tooltip>
                                        {!isFollowing ? "Follow Patient" : "Unfollow Patient"}
                                    </Tooltip>
                                }
                            >
                                <Button size="sm" variant={isFollowing ? "success" : "outline-secondary"} onClick={followPatient}>
                                    <span className="me-2">{isSavingFollowing ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={isFollowing ? faCheckCircle : faPlus} />}</span> {isFollowing ? "Following" : "Follow"}
                                </Button>
                            </OverlayTrigger>
                        )
                    }
                </Col>
                <Col md={2} className="text-right">
                    <Badge bg={"light"}><small><span className="text-muted">CSN: <PersonalIdentifier identifier={props.patient.csn} /></span></small></Badge>
                </Col>
            </Row>

            {props.patient && <ExplainEDDDashboard 
                tenant={props.tenant} patientVisit={props.patient} isQuickView={true}
                statuses={statuses} setStatuses={setStatuses} latestFileVersions={latestFileVersions} handleFileVersionChange={handleFileVersionChange}
                unreadFileVersionMap={unreadFileVersionMap} surveyResultsUpdateCallback={props.eddSurveyResultsUpdateCallback}
            />}
            {props.patient && <ExplainAdmitDCDashboard 
                tenant={props.tenant} patientVisit={props.patient} isQuickView={true}
                statuses={statuses} setStatuses={setStatuses} latestFileVersions={latestFileVersions} handleFileVersionChange={handleFileVersionChange}
                unreadFileVersionMap={unreadFileVersionMap} surveyResultsUpdateCallback={props.admitSurveyResultsUpdateCallback}
            />}
            {props.patient && <ExplainRevisitDashboard 
                tenant={props.tenant} patientVisit={props.patient} isQuickView={true}
                statuses={statuses} setStatuses={setStatuses} latestFileVersions={latestFileVersions} handleFileVersionChange={handleFileVersionChange}
                unreadFileVersionMap={unreadFileVersionMap} surveyResultsUpdateCallback={props.revisitSurveyResultsUpdateCallback}
            />}
            {
                props.patient && isLobby(props.patient) && (
                    <SectionTrackboardVerticalVideo user={props.user} className="mb-2" isLoadingDdx={isLoadingDdx} ddx={ddx} patientVisit={props.patient} feedbacks={feedbacks} setFeedbacks={setFeedbacks} statuses={statuses} latestFileVersions={latestFileVersions} handleShowScanMultiModal={handleShowScanMultiModalForSection} sectionToSetter={sectionToSetter} handleFileVersionChange={handleFileVersionChange} unreadFileVersionMap={unreadFileVersionMap} tenant={props.tenant} />
                )
            }
            <SectionPatientSummary className="mb-2" isLoadingPatientSummary={isLoadingPatientSummary} patientSummary={patientSummary} patientVisit={props.patient} feedbacks={feedbacks} setFeedbacks={setFeedbacks} statuses={statuses} setRegenerateTarget={setRegenerateTarget} setShowContextMenu={setShowRegenerateContextMenu} latestFileVersions={latestFileVersions} handleShowScanMultiModal={handleShowScanMultiModalForSection} sectionToSetter={sectionToSetter} handleFileVersionChange={handleFileVersionChange} unreadFileVersionMap={unreadFileVersionMap} user={props.user} tenant={props.tenant} />
            <SectionCurrentVisit className="mb-2" isLoadingCurrentVisitPresentation={isLoadingCurrentVisitPresentation} currentVisitPresentation={currentVisitPresentation} patientVisit={props.patient} feedbacks={feedbacks} setFeedbacks={setFeedbacks} statuses={statuses} setRegenerateTarget={setRegenerateTarget} setShowContextMenu={setShowRegenerateContextMenu} reasonsForVisit={reasonsForVisit} latestFileVersions={latestFileVersions} handleShowScanMultiModal={handleShowScanMultiModalForSection} sectionToSetter={sectionToSetter} handleFileVersionChange={handleFileVersionChange} unreadFileVersionMap={unreadFileVersionMap} user={props.user} tenant={props.tenant} />
            <SectionTrackboardDDX user={props.user} className="mb-2" isLoadingDdx={isLoadingDdx} ddx={ddx} patientVisit={props.patient} feedbacks={feedbacks} setFeedbacks={setFeedbacks} statuses={statuses} setRegenerateTarget={setRegenerateTarget} setShowContextMenu={setShowRegenerateContextMenu} latestFileVersions={latestFileVersions} handleShowScanMultiModal={handleShowScanMultiModalForSection} sectionToSetter={sectionToSetter} handleFileVersionChange={handleFileVersionChange} unreadFileVersionMap={unreadFileVersionMap} tenant={props.tenant} ddxActive={ddxActive} setDdxActive={setDdxActive} />
            <SectionTrackboardTimeline className="mb-2" isLoadingCharting={isLoadingCharting} charting={charting} patientVisit={props.patient} feedbacks={feedbacks} setFeedbacks={setFeedbacks} statuses={statuses} setRegenerateTarget={setRegenerateTarget} setShowContextMenu={setShowRegenerateContextMenu} locations={locations} latestFileVersions={latestFileVersions} handleShowScanMultiModal={handleShowScanMultiModalForSection} sectionToSetter={sectionToSetter} handleFileVersionChange={handleFileVersionChange} unreadFileVersionMap={unreadFileVersionMap} tenant={props.tenant} />
            <SectionTrackboardNextSteps className="mb-2" tenant={props.tenant} isLoadingDdx={isLoadingDdx} ddx={ddx} patientVisit={props.patient} feedbacks={feedbacks} setFeedbacks={setFeedbacks} statuses={statuses} setRegenerateTarget={setRegenerateTarget} setShowContextMenu={setShowRegenerateContextMenu} existingNextSteps={existingNextSteps} setExistingNextSteps={setExistingNextSteps} latestFileVersions={latestFileVersions} handleShowScanMultiModal={handleShowScanMultiModalForSection} sectionToSetter={sectionToSetter} handleFileVersionChange={handleFileVersionChange} unreadFileVersionMap={unreadFileVersionMap} />
            <ScanMultiModal patient={props.patient} isOpen={isScanMultiModalOpen} onClose={() => {setIsScanMultiModalOpen(false)}} versionInfo={selectedScanMultiVersion} initialSection={selectedInitialSection} triggeringEvents={selectedTriggeringEvents} />
            <Container fluid>
                <Row>
                    <Col md={12} className="mb-4 mr-4">
                        <Card className="trackboard-notes-container">
                            <Card.Body>
                                <ExpandingTextarea setText={handleNoteChange} text={noteText} sendChat={() => { } } placeholder={"Add your notes for this patient visit (visible only to you)..."} isDisabled={false} />
                                <Row style={{position: 'absolute', bottom: '12px', right: '12px'}}>
                                    <Col className="text-right text-muted">
                                        {
                                            isSavingNote ? (
                                                <><FontAwesomeIcon icon={faSpinner} spin />{' '}Saving...</>
                                            ) : noteText === lastSavedNoteText ? (
                                                <>Saved</>
                                            ) : (
                                                <>Not Saved</>
                                            )
                                        }
                                    </Col>
                                </Row>

                                {
                                    pastNotes && pastNotes.length > 0 && (
                                        <>
                                            <Row>
                                                <Col md={12} style={{paddingRight: '4px'}}>
                                                    {
                                                        pastNotes.map((note) => (
                                                            <>
                                                                <hr />
                                                                <Row>
                                                                    <Col style={{color: '#999999'}}>
                                                                        <strong>Previous Note</strong> &bull; CSN {note.csn} &bull; Last updated at: {getUserFriendlyDate(note.creation_time)}
                                                                    </Col>
                                                                </Row>
                                                                <Row>
                                                                    <Col>
                                                                        {note.note}
                                                                    </Col>
                                                                </Row>
                                                            </>
                                                        ))
                                                    }
                                                </Col>
                                            </Row>
                                        </>
                                    )
                                }
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
}, (prev, next) => {
    const samePatientKeys = prev.patient ? (prev.patient.csn === next.patient.csn) : false;

    // Only skip re-rendering if patient identity is unchanged
    return samePatientKeys;
});
